// Dashboard privado del eddy_admin — solo accesible por el email
// EDDY_ADMIN_EMAIL (ercphillipsuk@gmail.com). 4 secciones:
//   1. Biografía (bio + foto perfil + foto portada)
//   2. Publicaciones (crear + editar inline + eliminar)
//   3. Cocina en Vivo (URL de YouTube)
//   4. Stats (agregados del canal 'eddy' — solo su sección)
//
// Reutiliza los mismos endpoints /api/eddy/* que la landing pública.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, User, ImagePlus, Save, Loader2, Pencil, Trash2, X, Plus,
  BarChart3, MessageCircle, Heart, Share2, Eye, FileText, Youtube, Image as ImageIcon,
  Send, Bell, AlertCircle, CheckCircle2, Briefcase, Inbox, Mail, Languages,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";

const TABS = [
  { key: "bio",        label: "Biografía",       icon: User },
  { key: "epb",        label: "EPB Global F&B",  icon: Briefcase },
  { key: "posts",      label: "Publicaciones",   icon: FileText },
  { key: "cocina",     label: "Cocina en Vivo",  icon: Youtube },
  { key: "broadcast",  label: "Notificar",       icon: Bell },
  { key: "leads",      label: "Consultas",       icon: Inbox },
  { key: "stats",      label: "Estadísticas",    icon: BarChart3 },
];

const RebelByEddyDashboardPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("bio");

  // AuthContext convention (per its comment): user===null → loading,
  // user===false → guest, user={…} → authenticated. Only redirect once
  // the auth check has resolved so we don't kick out the admin mid-hydration.
  if (user === null) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
      </div>
    );
  }
  if (!user || !user.is_eddy_admin) {
    return <Navigate to="/rebel-by-eddy" replace />;
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="eddy-dashboard-page">
      <Navbar />
      <header className="border-b border-white/5 bg-gradient-to-b from-amber-950/20 via-black to-black">
        <div className="mx-auto max-w-5xl px-4 pb-6 pt-24 md:px-8">
          <div className="flex items-center justify-between gap-3">
            <Link
              to="/rebel-by-eddy"
              data-testid="dashboard-back-to-public"
              className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/45 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Volver a la página pública
            </Link>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/[0.1] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
              Panel privado
            </span>
          </div>
          <h1 className="mt-3 font-mono text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
            Dashboard <span className="text-amber-400">Eddy</span>
          </h1>
          <p className="mt-1 text-sm text-white/55">
            Gestiona tu biografía, publicaciones y directo. Ámbito exclusivo de RE₿EL by Eddy.
          </p>
        </div>
        <nav
          className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-4 pb-1 md:px-8"
          data-testid="dashboard-tabs"
        >
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`dashboard-tab-${key}`}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${
                tab === key
                  ? "border-amber-400 text-amber-200"
                  : "border-transparent text-white/45 hover:text-white/80"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 md:px-8">
        {tab === "bio" && <BioTab />}
        {tab === "epb" && <EpbTab />}
        {tab === "posts" && <PostsTab />}
        {tab === "cocina" && <CocinaTab />}
        {tab === "broadcast" && <BroadcastTab />}
        {tab === "leads" && <LeadsTab />}
        {tab === "stats" && <StatsTab />}
      </main>
    </div>
  );
};

const LOCALES = [
  { code: "es", label: "Español" },
  { code: "en", label: "English" },
  { code: "sk", label: "Slovenčina" },
];

// ══════════════════════════════════════════════════════════════════════
// BIO TAB — text (per-locale ES/EN/SK) + profile photo + banner
// ══════════════════════════════════════════════════════════════════════
const BioTab = () => {
  const [profile, setProfile] = useState(null);
  const [locale, setLocale] = useState("es");
  const [bioMap, setBioMap] = useState({ es: "", en: "", sk: "", de: "", "zh-CN": "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const photoRef = useRef(null);
  const bannerRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/eddy/profile`, { withCredentials: true });
    setProfile(data);
    const i18n = data.bio_i18n || {};
    const legacy = data.bio || "";
    setBioMap({
      es: i18n.es ?? legacy,   // legacy default to ES (assumption)
      en: i18n.en ?? "",
      sk: i18n.sk ?? "",
      de: i18n.de ?? "",
      "zh-CN": i18n["zh-CN"] ?? "",
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const setLocaleBio = (code, value) =>
    setBioMap((prev) => ({ ...prev, [code]: value }));

  const saveBio = async () => {
    setSaving(true);
    setMsg("");
    try {
      const { data } = await axios.put(
        `${API}/eddy/profile`,
        // Send all three locales at once + clear legacy field
        { bio_i18n: bioMap, bio: "" },
        { withCredentials: true }
      );
      setProfile(data);
      setMsg("Biografía guardada (ES · EN · SK)");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file, kind) => {
    if (!file) return;
    const setUp = kind === "photo" ? setUploadingPhoto : setUploadingBanner;
    setUp(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const path = kind === "photo" ? "photo" : "banner";
      const { data } = await axios.post(`${API}/eddy/profile/${path}`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile(data);
      setMsg(kind === "photo" ? "Foto de perfil actualizada" : "Foto de portada actualizada");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error al subir la imagen");
    } finally {
      setUp(false);
    }
  };

  if (!profile) return <TabLoader />;

  return (
    <div className="space-y-6" data-testid="dashboard-bio-tab">
      <Card title="Foto de perfil" subtitle="Aparece dentro del bloque de Biografía">
        <div className="flex flex-wrap items-start gap-5">
          <div className="relative aspect-square w-32 shrink-0 overflow-hidden rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-950/40 to-black">
            {profile.photo_url ? (
              <img src={profile.photo_url} alt="perfil" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full items-center justify-center text-4xl font-black text-amber-500/40">E</div>
            )}
          </div>
          <div>
            <button
              type="button"
              onClick={() => photoRef.current?.click()}
              disabled={uploadingPhoto}
              data-testid="dashboard-photo-upload-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/45 bg-amber-500/[0.14] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-200 transition hover:bg-amber-500/[0.22] disabled:opacity-60"
            >
              {uploadingPhoto ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImagePlus className="h-3 w-3" />}
              {profile.photo_url ? "Cambiar foto de perfil" : "Subir foto de perfil"}
            </button>
            <input
              ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp"
              className="hidden" data-testid="dashboard-photo-input"
              onChange={(e) => uploadImage(e.target.files?.[0], "photo")}
            />
            <p className="mt-2 text-xs text-white/40">JPG, PNG o WebP · Máx 5 MB</p>
          </div>
        </div>
      </Card>

      <Card title="Foto de portada (banner)" subtitle="Cabecera de tu página pública">
        <div className="space-y-3">
          <div className="relative h-32 w-full overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-amber-950/30 via-black to-black">
            {profile.banner_url ? (
              <img src={profile.banner_url} alt="banner" className="h-full w-full object-cover opacity-90" />
            ) : (
              <div className="flex h-full items-center justify-center text-xs text-white/40">
                <ImageIcon className="mr-2 h-4 w-4" /> Sin portada
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => bannerRef.current?.click()}
            disabled={uploadingBanner}
            data-testid="dashboard-banner-upload-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/45 bg-amber-500/[0.14] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-200 transition hover:bg-amber-500/[0.22] disabled:opacity-60"
          >
            {uploadingBanner ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImagePlus className="h-3 w-3" />}
            {profile.banner_url ? "Cambiar portada" : "Subir portada"}
          </button>
          <input
            ref={bannerRef} type="file" accept="image/jpeg,image/png,image/webp"
            className="hidden" data-testid="dashboard-banner-input"
            onChange={(e) => uploadImage(e.target.files?.[0], "banner")}
          />
          <p className="text-xs text-white/40">JPG, PNG o WebP · Máx 8 MB · Aspect ~16:9 recomendado</p>
        </div>
      </Card>

      <Card
        title="Texto biográfico"
        subtitle="Una versión por idioma (ES · EN · SK). Si dejas un idioma vacío, se muestra el texto canónico traducido de la app."
      >
        <LocaleTabs value={locale} onChange={setLocale} testidPrefix="dashboard-bio-locale" />
        <textarea
          value={bioMap[locale] || ""}
          onChange={(e) => setLocaleBio(locale, e.target.value)}
          rows={12}
          maxLength={4000}
          data-testid={`dashboard-bio-textarea-${locale}`}
          className="mt-3 w-full resize-y rounded-xl border border-white/10 bg-black/40 p-3 text-sm leading-relaxed text-white/85 outline-none focus:border-amber-400/50"
          placeholder={`Historia, trayectoria, filosofía… (${locale.toUpperCase()})`}
        />
        <div className="mt-1 flex items-center justify-between">
          <span className="font-mono text-[10px] text-white/35">{(bioMap[locale] || "").length} / 4000</span>
          <div className="flex items-center gap-3">
            {msg && (
              <span className="text-xs text-amber-300" data-testid="dashboard-bio-msg">{msg}</span>
            )}
            <button
              type="button"
              onClick={saveBio}
              disabled={saving}
              data-testid="dashboard-bio-save-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/50 bg-amber-500/[0.14] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.22em] text-amber-200 transition hover:bg-amber-500/[0.22] disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Guardar biografía (ES · EN · SK)
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ── Locale sub-tab picker (shared by bio + EPB editors) ────────────
const LocaleTabs = ({ value, onChange, testidPrefix }) => (
  <div className="inline-flex gap-1 rounded-lg border border-white/10 bg-black/40 p-1">
    {LOCALES.map((l) => (
      <button
        key={l.code}
        type="button"
        onClick={() => onChange(l.code)}
        data-testid={`${testidPrefix}-${l.code}`}
        className={`rounded-md px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] transition ${
          value === l.code
            ? "bg-amber-500/[0.16] text-amber-200 border border-amber-400/40"
            : "text-white/50 hover:text-white/80"
        }`}
      >
        {l.code.toUpperCase()} · {l.label}
      </button>
    ))}
  </div>
);

// ══════════════════════════════════════════════════════════════════════
// POSTS TAB — list + create + edit + delete
// ══════════════════════════════════════════════════════════════════════
const PostsTab = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [translatingId, setTranslatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/eddy/feed?limit=50`);
      setPosts(data.posts || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!confirm("¿Eliminar esta publicación?")) return;
    await axios.delete(`${API}/eddy/posts/${id}`, { withCredentials: true });
    setPosts((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <div className="space-y-5" data-testid="dashboard-posts-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-white/55">
          {loading ? "…" : `${posts.length} publicación${posts.length === 1 ? "" : "es"} en el canal`}
        </p>
        <button
          type="button"
          onClick={() => setShowComposer((v) => !v)}
          data-testid="dashboard-composer-toggle"
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/45 bg-amber-500/[0.14] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-200 transition hover:bg-amber-500/[0.22]"
        >
          {showComposer ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
          {showComposer ? "Cerrar composer" : "Nueva publicación"}
        </button>
      </div>

      {showComposer && (
        <PostComposer
          onCreated={() => { setShowComposer(false); load(); }}
        />
      )}

      {loading ? (
        <TabLoader />
      ) : posts.length === 0 ? (
        <Card>
          <p className="text-center text-sm text-white/50">Aún no has publicado nada.</p>
        </Card>
      ) : (
        <ul className="space-y-3">
          {posts.map((p) => (
            <li key={p.id} data-testid={`dashboard-post-${p.id}`}>
              {editingId === p.id ? (
                <PostEditor
                  post={p}
                  onCancel={() => setEditingId(null)}
                  onSaved={(updated) => {
                    setPosts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
                    setEditingId(null);
                  }}
                />
              ) : translatingId === p.id ? (
                <PostTranslationsEditor
                  post={p}
                  onCancel={() => setTranslatingId(null)}
                  onSaved={(updated) => {
                    setPosts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
                  }}
                />
              ) : (
                <PostRow
                  post={p}
                  onEdit={() => setEditingId(p.id)}
                  onTranslate={() => setTranslatingId(p.id)}
                  onRemove={() => remove(p.id)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PostRow = ({ post, onEdit, onTranslate, onRemove }) => (
  <Card>
    <div className="flex items-start gap-3">
      {post.image_url && (
        <img
          src={post.image_url}
          alt=""
          className="h-20 w-20 shrink-0 rounded-md object-cover"
        />
      )}
      <div className="min-w-0 flex-1">
        <h4 className="line-clamp-2 text-sm font-bold text-white">{post.title}</h4>
        {post.ticker && (
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400/80">{post.ticker}</p>
        )}
        <div className="mt-2 flex flex-wrap gap-3 font-mono text-[10px] text-white/40">
          <span><Heart className="mr-1 inline h-3 w-3" />{post.reaction_count || 0}</span>
          <span><MessageCircle className="mr-1 inline h-3 w-3" />{post.comment_count || 0}</span>
          <span><Share2 className="mr-1 inline h-3 w-3" />{post.shares_count || 0}</span>
          <span><Eye className="mr-1 inline h-3 w-3" />{post.view_count || 0}</span>
          <TranslationsBadge post={post} />
        </div>
      </div>
      <div className="flex shrink-0 gap-1.5">
        <button
          type="button"
          onClick={onTranslate}
          data-testid={`dashboard-post-translate-${post.id}`}
          title="Editar traducciones (ES · EN · SK)"
          className="rounded-md border border-white/10 p-1.5 text-white/60 transition hover:border-amber-400/40 hover:text-amber-200"
        >
          <Languages className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onEdit}
          data-testid={`dashboard-post-edit-${post.id}`}
          title="Editar"
          className="rounded-md border border-white/10 p-1.5 text-white/60 transition hover:border-amber-400/40 hover:text-amber-200"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          data-testid={`dashboard-post-delete-${post.id}`}
          title="Eliminar"
          className="rounded-md border border-white/10 p-1.5 text-white/60 transition hover:border-red-400/40 hover:text-red-300"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  </Card>
);

// Small pill that shows which locales are already populated for the post.
// Helps Eddy quickly spot posts still missing a translation.
const TranslationsBadge = ({ post }) => {
  const langs = ["es", "en", "sk"];
  const present = langs.filter((l) => {
    const t = (post.title_i18n || {})[l];
    const d = (post.description_i18n || {})[l];
    return (typeof t === "string" && t.trim()) && (typeof d === "string" && d.trim());
  });
  return (
    <span
      data-testid={`dashboard-post-translations-badge-${post.id}`}
      className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/30 px-1.5 py-0.5"
    >
      <Languages className="h-3 w-3" />
      {langs.map((l) => (
        <span
          key={l}
          className={`font-mono text-[9px] ${present.includes(l) ? "text-amber-300" : "text-white/25"}`}
        >
          {l.toUpperCase()}
        </span>
      ))}
    </span>
  );
};

const PostComposer = ({ onCreated }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ticker, setTicker] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const onFile = (f) => {
    setFile(f || null);
    setPreview(f ? URL.createObjectURL(f) : null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file || title.trim().length < 3 || description.trim().length < 10) {
      setErr("Título ≥3, descripción ≥10 y una imagen/vídeo son obligatorios");
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      if (ticker.trim()) fd.append("ticker", ticker.trim().toUpperCase());
      await axios.post(`${API}/eddy/posts`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTitle(""); setDescription(""); setTicker(""); setFile(null); setPreview(null);
      onCreated?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Error al publicar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title="Nueva publicación">
      <form onSubmit={submit} className="space-y-3" data-testid="dashboard-composer-form">
        <div className="grid gap-3 md:grid-cols-2">
          <input
            type="text" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Título" maxLength={120}
            data-testid="dashboard-composer-title"
            className="rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
          />
          <input
            type="text" value={ticker} onChange={(e) => setTicker(e.target.value)}
            placeholder="Ticker (opcional)" maxLength={12}
            data-testid="dashboard-composer-ticker"
            className="rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
          />
        </div>
        <textarea
          value={description} onChange={(e) => setDescription(e.target.value)}
          rows={5} maxLength={5000} placeholder="Descripción…"
          data-testid="dashboard-composer-description"
          className="w-full resize-y rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
        />
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.04] px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-white/70 transition hover:border-amber-400/40 hover:text-amber-200">
            <ImagePlus className="h-3 w-3" />
            {file ? "Cambiar archivo" : "Añadir imagen/vídeo"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
              onChange={(e) => onFile(e.target.files?.[0])}
              className="hidden"
              data-testid="dashboard-composer-file"
            />
          </label>
          {preview && (
            <img src={preview} alt="preview" className="h-16 w-16 rounded-md border border-white/10 object-cover" />
          )}
        </div>
        {err && <p className="text-xs text-red-400" data-testid="dashboard-composer-error">{err}</p>}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            data-testid="dashboard-composer-submit"
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/55 bg-amber-500/[0.16] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.22em] text-amber-200 transition hover:bg-amber-500/[0.24] disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            Publicar
          </button>
        </div>
      </form>
    </Card>
  );
};

const PostEditor = ({ post, onCancel, onSaved }) => {
  const [title, setTitle] = useState(post.title || "");
  const [description, setDescription] = useState(post.description || "");
  const [ticker, setTicker] = useState(post.ticker || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await axios.put(
        `${API}/eddy/posts/${post.id}`,
        { title, description, ticker },
        { withCredentials: true }
      );
      onSaved?.(data.post);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Editar publicación">
      <div className="space-y-3" data-testid={`dashboard-post-editor-${post.id}`}>
        <input
          type="text" value={title} onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          data-testid={`dashboard-post-edit-title-${post.id}`}
          className="w-full rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
        />
        <textarea
          value={description} onChange={(e) => setDescription(e.target.value)}
          rows={6} maxLength={5000}
          data-testid={`dashboard-post-edit-description-${post.id}`}
          className="w-full resize-y rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
        />
        <input
          type="text" value={ticker} onChange={(e) => setTicker(e.target.value)}
          maxLength={12} placeholder="Ticker (opcional)"
          data-testid={`dashboard-post-edit-ticker-${post.id}`}
          className="w-40 rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
        />
        {err && <p className="text-xs text-red-400">{err}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button" onClick={onCancel} disabled={saving}
            data-testid={`dashboard-post-edit-cancel-${post.id}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.04] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white/60 transition hover:text-white"
          >
            <X className="h-3 w-3" /> Cancelar
          </button>
          <button
            type="button" onClick={save} disabled={saving}
            data-testid={`dashboard-post-edit-save-${post.id}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/50 bg-amber-500/[0.14] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-200 transition hover:bg-amber-500/[0.22] disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Guardar cambios
          </button>
        </div>
      </div>
    </Card>
  );
};

// ══════════════════════════════════════════════════════════════════════
// PostTranslationsEditor — editor manual por idioma (ES · EN · SK).
// Sobrescribe la traducción automática de Claude para el idioma
// seleccionado. Los otros idiomas quedan intactos.
// ══════════════════════════════════════════════════════════════════════
const PostTranslationsEditor = ({ post, onCancel, onSaved }) => {
  const [locale, setLocale] = useState("es");
  const [titleMap, setTitleMap] = useState({
    es: (post.title_i18n || {}).es || post.title || "",
    en: (post.title_i18n || {}).en || "",
    sk: (post.title_i18n || {}).sk || "",
    de: (post.title_i18n || {}).de || "",
    "zh-CN": (post.title_i18n || {})["zh-CN"] || "",
  });
  const [descMap, setDescMap] = useState({
    es: (post.description_i18n || {}).es || post.description || "",
    en: (post.description_i18n || {}).en || "",
    sk: (post.description_i18n || {}).sk || "",
    de: (post.description_i18n || {}).de || "",
    "zh-CN": (post.description_i18n || {})["zh-CN"] || "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const setLocaleTitle = (v) => setTitleMap((prev) => ({ ...prev, [locale]: v }));
  const setLocaleDesc = (v) => setDescMap((prev) => ({ ...prev, [locale]: v }));

  const saveLocale = async () => {
    const t = (titleMap[locale] || "").trim();
    const d = (descMap[locale] || "").trim();
    if (t.length < 1 || d.length < 1) {
      setMsg("Título y descripción no pueden estar vacíos");
      return;
    }
    setSaving(true);
    setMsg("");
    try {
      const { data } = await axios.put(
        `${API}/eddy/posts/${post.id}/translation/${locale}`,
        { title: t, description: d },
        { withCredentials: true }
      );
      onSaved?.(data.post);
      setMsg(`Traducción ${locale.toUpperCase()} guardada`);
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Traducciones (ES · EN · SK)" subtitle="Sobrescribe la traducción automática de Claude cuando algún término culinario o nombre propio no quede bien.">
      <div className="space-y-3" data-testid={`dashboard-post-translations-${post.id}`}>
        <LocaleTabs value={locale} onChange={setLocale} testidPrefix={`dashboard-post-translations-locale-${post.id}`} />

        <div className="space-y-2">
          <label className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white/45">
            Título · {locale.toUpperCase()}
          </label>
          <input
            type="text"
            value={titleMap[locale] || ""}
            onChange={(e) => setLocaleTitle(e.target.value)}
            maxLength={120}
            data-testid={`dashboard-post-translations-title-${locale}-${post.id}`}
            className="w-full rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
          />
          <span className="font-mono text-[10px] text-white/35">
            {(titleMap[locale] || "").length} / 120
          </span>
        </div>

        <div className="space-y-2">
          <label className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white/45">
            Descripción · {locale.toUpperCase()}
          </label>
          <textarea
            value={descMap[locale] || ""}
            onChange={(e) => setLocaleDesc(e.target.value)}
            rows={8}
            maxLength={5000}
            data-testid={`dashboard-post-translations-description-${locale}-${post.id}`}
            className="w-full resize-y rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm leading-relaxed text-white outline-none focus:border-amber-400/50"
          />
          <span className="font-mono text-[10px] text-white/35">
            {(descMap[locale] || "").length} / 5000
          </span>
        </div>

        {msg && (
          <p className="text-xs text-amber-300" data-testid={`dashboard-post-translations-msg-${post.id}`}>{msg}</p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button" onClick={onCancel} disabled={saving}
            data-testid={`dashboard-post-translations-cancel-${post.id}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.04] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white/60 transition hover:text-white"
          >
            <X className="h-3 w-3" /> Cerrar
          </button>
          <button
            type="button" onClick={saveLocale} disabled={saving}
            data-testid={`dashboard-post-translations-save-${post.id}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/50 bg-amber-500/[0.14] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-200 transition hover:bg-amber-500/[0.22] disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Guardar {locale.toUpperCase()}
          </button>
        </div>
      </div>
    </Card>
  );
};

// ══════════════════════════════════════════════════════════════════════
// COCINA TAB — YouTube URL
// ══════════════════════════════════════════════════════════════════════
const CocinaTab = () => {
  const [profile, setProfile] = useState(null);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/eddy/profile`, { withCredentials: true }).then(({ data }) => {
      if (!alive) return;
      setProfile(data);
      setUrl(data.youtube_id ? `https://youtu.be/${data.youtube_id}` : "");
      setTitle(data.youtube_title || "");
    });
    return () => { alive = false; };
  }, []);

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      const { data } = await axios.put(
        `${API}/eddy/profile`,
        { youtube_url: url, youtube_title: title },
        { withCredentials: true }
      );
      setProfile(data);
      setMsg("Directo actualizado");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!confirm("¿Quitar el directo?")) return;
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/eddy/profile`,
        { youtube_url: "", youtube_title: "" },
        { withCredentials: true }
      );
      setProfile(data);
      setUrl(""); setTitle("");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <TabLoader />;

  return (
    <div className="space-y-5" data-testid="dashboard-cocina-tab">
      <Card title="Directo en YouTube" subtitle="URL o ID del vídeo · Título opcional">
        <div className="space-y-3">
          <label className="block">
            <span className="font-mono text-[10px] font-black uppercase tracking-[0.22em] text-amber-400">
              URL o ID de YouTube
            </span>
            <input
              type="text" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://youtu.be/… o dQw4w9WgXcQ"
              data-testid="dashboard-cocina-url"
              className="mt-1 w-full rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] font-black uppercase tracking-[0.22em] text-amber-400">
              Título (opcional)
            </span>
            <input
              type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="Miércoles 20h · Reacción al FOMC"
              data-testid="dashboard-cocina-title"
              className="mt-1 w-full rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
            />
          </label>
          <div className="flex flex-wrap justify-end gap-2">
            {profile.youtube_id && (
              <button
                type="button" onClick={clear} disabled={saving}
                data-testid="dashboard-cocina-clear"
                className="inline-flex items-center gap-1.5 rounded-md border border-red-400/30 bg-red-500/[0.06] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-red-300 transition hover:bg-red-500/[0.14]"
              >
                <Trash2 className="h-3 w-3" /> Quitar directo
              </button>
            )}
            {msg && <span className="self-center text-xs text-amber-300">{msg}</span>}
            <button
              type="button" onClick={save} disabled={saving}
              data-testid="dashboard-cocina-save"
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/50 bg-amber-500/[0.14] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.22em] text-amber-200 transition hover:bg-amber-500/[0.22] disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Guardar directo
            </button>
          </div>
        </div>
      </Card>

      {profile.youtube_id && (
        <Card title="Vista previa">
          <div className="aspect-video w-full overflow-hidden rounded-xl border border-white/10 bg-black">
            <iframe
              src={`https://www.youtube.com/embed/${profile.youtube_id}`}
              title={profile.youtube_title || "Cocina en Vivo"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="h-full w-full"
              data-testid="dashboard-cocina-preview"
            />
          </div>
        </Card>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// BROADCAST TAB — notify users who have interacted with Eddy's posts
// ══════════════════════════════════════════════════════════════════════
const BroadcastTab = () => {
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const [pv, hi] = await Promise.all([
        axios.get(`${API}/eddy/broadcast/preview`, { withCredentials: true }),
        axios.get(`${API}/eddy/broadcast/history`, { withCredentials: true }),
      ]);
      setPreview(pv.data);
      setHistory(hi.data.items || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (title.trim().length < 3 || message.trim().length < 5) {
      setErr("Título ≥3, mensaje ≥5 caracteres");
      return;
    }
    if (!confirm(`Enviar aviso a ${preview?.audience_count ?? "?"} usuarios (notif in-app + email)?`)) return;
    setSending(true);
    setErr("");
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/eddy/broadcast`,
        { title: title.trim(), message: message.trim(), image_url: imageUrl || null },
        { withCredentials: true }
      );
      setResult(data);
      setTitle(""); setMessage(""); setImageUrl("");
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  const rateLimited = !!preview?.rate_limited_until;
  const noAudience = preview && preview.audience_count === 0;

  return (
    <div className="space-y-6" data-testid="dashboard-broadcast-tab">
      <Card title="Alcance del broadcast" subtitle="Usuarios que han interactuado con al menos un post tuyo (like · comentario · guardado · compartido)">
        {preview === null ? (
          <TabLoader />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div
              data-testid="broadcast-audience-count"
              className="rounded-xl border border-amber-400/25 bg-amber-500/[0.06] p-4"
            >
              <User className="h-4 w-4 text-amber-300" />
              <div className="mt-2 font-mono text-2xl font-black text-amber-100">{preview.audience_count}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-200/70">Destinatarios</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <Bell className="h-4 w-4 text-white/60" />
              <div className={`mt-2 font-mono text-sm font-bold ${preview.email_provider_ready ? "text-emerald-300" : "text-red-300"}`}>
                {preview.email_provider_ready ? "Listo" : "Sin configurar"}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">Email provider</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <AlertCircle className="h-4 w-4 text-white/60" />
              <div className={`mt-2 font-mono text-sm font-bold ${rateLimited ? "text-orange-300" : "text-emerald-300"}`}>
                {rateLimited ? "Pausado" : "Disponible"}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
                {rateLimited ? `Espera hasta ${new Date(preview.rate_limited_until).toLocaleTimeString()}` : "Rate limit: 1/hora"}
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card title="Nuevo aviso" subtitle="Notif in-app + email. Se envía a todos los interactores del canal.">
        <div className="space-y-3" data-testid="broadcast-form">
          <input
            type="text" value={title} onChange={(e) => setTitle(e.target.value)}
            maxLength={80} placeholder="Título del aviso (ej. Directo mañana 20h)"
            data-testid="broadcast-title-input"
            className="w-full rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
          />
          <textarea
            value={message} onChange={(e) => setMessage(e.target.value)}
            rows={5} maxLength={500}
            placeholder="Mensaje corto (máx 500 caracteres). Aparece en la notificación y en el cuerpo del email."
            data-testid="broadcast-message-input"
            className="w-full resize-y rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
          />
          <input
            type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)}
            maxLength={800} placeholder="URL de imagen opcional (Cloudinary, etc.)"
            data-testid="broadcast-image-input"
            className="w-full rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
          />
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] text-white/40">
              {title.length}/80 · {message.length}/500
            </span>
            <button
              type="button"
              onClick={send}
              disabled={sending || rateLimited || noAudience}
              title={rateLimited ? "Rate limit activo" : noAudience ? "Aún no hay interactores" : ""}
              data-testid="broadcast-send-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/55 bg-amber-500/[0.16] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.22em] text-amber-200 transition hover:bg-amber-500/[0.24] disabled:opacity-40"
            >
              {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              Enviar aviso
            </button>
          </div>
          {err && (
            <p className="text-xs text-red-400" data-testid="broadcast-error">{err}</p>
          )}
          {result && (
            <div
              data-testid="broadcast-result"
              className="rounded-md border border-emerald-400/30 bg-emerald-500/[0.06] p-3 text-xs text-emerald-200"
            >
              <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
              Enviado a {result.users_notified} usuarios · {result.emails_sent} emails entregados · {result.errors_count} errores
            </div>
          )}
        </div>
      </Card>

      {history.length > 0 && (
        <Card title="Historial de avisos" subtitle="Últimos broadcasts enviados desde este dashboard">
          <ul className="space-y-2" data-testid="broadcast-history">
            {history.map((h) => (
              <li
                key={h.id}
                data-testid={`broadcast-history-${h.id}`}
                className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="truncate text-sm text-white/85">{h.title}</span>
                  <span className="shrink-0 font-mono text-[10px] text-white/40">
                    {new Date(h.sent_at).toLocaleString()}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-3 font-mono text-[10px] text-white/50">
                  <span>notif: {h.users_notified}</span>
                  <span>email: {h.emails_sent}</span>
                  {h.errors_count > 0 && <span className="text-red-300">errors: {h.errors_count}</span>}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// EPB TAB — edit intro + services list (EPB Global F&B block)
// ══════════════════════════════════════════════════════════════════════
const EpbTab = () => {
  const [locale, setLocale] = useState("es");
  const [introMap, setIntroMap] = useState({ es: "", en: "", sk: "", de: "", "zh-CN": "" });
  const [servicesMap, setServicesMap] = useState({ es: [], en: [], sk: [], de: [], "zh-CN": [] });
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoMsg, setLogoMsg] = useState("");
  const logoRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/eddy/profile`, { withCredentials: true }).then(({ data }) => {
      if (!alive) return;
      const introI18n = data.epb_intro_i18n || {};
      const svcI18n = data.epb_services_i18n || {};
      const legacyIntro = data.epb_intro || "";
      const legacySvc = data.epb_services || [];
      setLogoUrl(data.epb_logo_url || null);
      setIntroMap({
        es: introI18n.es ?? legacyIntro,
        en: introI18n.en ?? "",
        sk: introI18n.sk ?? "",
        de: introI18n.de ?? "",
        "zh-CN": introI18n["zh-CN"] ?? "",
      });
      setServicesMap({
        es: svcI18n.es ?? legacySvc,
        en: svcI18n.en ?? [],
        sk: svcI18n.sk ?? [],
        de: svcI18n.de ?? [],
        "zh-CN": svcI18n["zh-CN"] ?? [],
      });
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const intro = introMap[locale] || "";
  const services = servicesMap[locale] || [];

  const setIntro = (v) => setIntroMap((p) => ({ ...p, [locale]: v }));
  const setService = (i, patch) =>
    setServicesMap((p) => ({
      ...p,
      [locale]: (p[locale] || []).map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }));
  const addService = () =>
    setServicesMap((p) => ({ ...p, [locale]: [...(p[locale] || []), { title: "", description: "", details: "" }] }));
  const removeService = (i) =>
    setServicesMap((p) => ({ ...p, [locale]: (p[locale] || []).filter((_, idx) => idx !== i) }));
  const move = (i, dir) =>
    setServicesMap((p) => {
      const arr = [...(p[locale] || [])];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return p;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...p, [locale]: arr };
    });

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      // Clean empty-title services per locale
      const cleanSvc = Object.fromEntries(
        Object.entries(servicesMap).map(([k, arr]) => [k, (arr || []).filter((s) => (s.title || "").trim())])
      );
      const { data } = await axios.put(
        `${API}/eddy/profile`,
        {
          epb_intro_i18n: introMap,
          epb_services_i18n: cleanSvc,
          epb_intro: "",       // clear legacy so i18n takes precedence
          epb_services: [],    // clear legacy
        },
        { withCredentials: true }
      );
      setIntroMap({
        es: (data.epb_intro_i18n || {}).es || "",
        en: (data.epb_intro_i18n || {}).en || "",
        sk: (data.epb_intro_i18n || {}).sk || "",
        de: (data.epb_intro_i18n || {}).de || "",
        "zh-CN": (data.epb_intro_i18n || {})["zh-CN"] || "",
      });
      setServicesMap({
        es: (data.epb_services_i18n || {}).es || [],
        en: (data.epb_services_i18n || {}).en || [],
        sk: (data.epb_services_i18n || {}).sk || [],
        de: (data.epb_services_i18n || {}).de || [],
        "zh-CN": (data.epb_services_i18n || {})["zh-CN"] || [],
      });
      setMsg("EPB Global F&B guardado (ES · EN · SK)");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!confirm("Restablecer la sección al texto por defecto (traducciones i18n)?")) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/eddy/profile`,
        {
          epb_intro_i18n: { es: "", en: "", sk: "", de: "", "zh-CN": "" },
          epb_services_i18n: { es: [], en: [], sk: [], de: [], "zh-CN": [] },
          epb_intro: "",
          epb_services: [],
        },
        { withCredentials: true }
      );
      setIntroMap({ es: "", en: "", sk: "", de: "", "zh-CN": "" });
      setServicesMap({ es: [], en: [], sk: [], de: [], "zh-CN": [] });
      setMsg("Restablecido — se mostrará el texto por defecto (i18n)");
      setTimeout(() => setMsg(""), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <TabLoader />;

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    setLogoMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/eddy/profile/epb-logo`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.epb_logo_url || null);
      setLogoMsg("Logotipo actualizado");
      setTimeout(() => setLogoMsg(""), 2500);
    } catch (e) {
      setLogoMsg(e?.response?.data?.detail || "Error al subir el logotipo");
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!confirm("¿Eliminar el logotipo actual? Podrás subir otro más tarde.")) return;
    setLogoUploading(true);
    setLogoMsg("");
    try {
      const { data } = await axios.delete(`${API}/eddy/profile/epb-logo`, { withCredentials: true });
      setLogoUrl(data.epb_logo_url || null);
      setLogoMsg("Logotipo eliminado");
      setTimeout(() => setLogoMsg(""), 2500);
    } catch (e) {
      setLogoMsg(e?.response?.data?.detail || "Error al eliminar el logotipo");
    } finally {
      setLogoUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-epb-tab">
      <Card
        title="Logotipo EPB Global F&B"
        subtitle="Aparece en el encabezado del bloque de consultoría en la página pública. PNG con transparencia recomendado."
      >
        <div className="flex flex-wrap items-start gap-5">
          <div
            className="relative flex aspect-square w-32 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-amber-500/25 bg-black/50 p-3"
            data-testid="dashboard-epb-logo-preview"
          >
            {logoUrl ? (
              <img src={logoUrl} alt="logo EPB" className="h-full w-full object-contain" />
            ) : (
              <div className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
                Sin logo
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <button
              type="button"
              onClick={() => logoRef.current?.click()}
              disabled={logoUploading}
              data-testid="dashboard-epb-logo-upload-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/45 bg-amber-500/[0.14] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-200 transition hover:bg-amber-500/[0.22] disabled:opacity-60"
            >
              {logoUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImagePlus className="h-3 w-3" />}
              {logoUrl ? "Reemplazar logotipo" : "Subir logotipo"}
            </button>
            {logoUrl && (
              <button
                type="button"
                onClick={removeLogo}
                disabled={logoUploading}
                data-testid="dashboard-epb-logo-remove-btn"
                className="ml-2 inline-flex items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.04] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white/60 transition hover:border-red-400/40 hover:text-red-300 disabled:opacity-60"
              >
                <Trash2 className="h-3 w-3" /> Eliminar
              </button>
            )}
            <input
              ref={logoRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid="dashboard-epb-logo-input"
              onChange={(e) => uploadLogo(e.target.files?.[0])}
            />
            <p className="text-xs text-white/40">JPG, PNG o WebP · Máx 5 MB · Preferible fondo transparente</p>
            {logoMsg && (
              <p className="text-xs text-amber-300" data-testid="dashboard-epb-logo-msg">{logoMsg}</p>
            )}
          </div>
        </div>
      </Card>

      <LocaleTabs value={locale} onChange={setLocale} testidPrefix="dashboard-epb-locale" />

      <Card
        title={`Introducción · EPB Global F&B · ${locale.toUpperCase()}`}
        subtitle="Deja vacío en un idioma para que se muestre la versión canónica de i18n."
      >
        <textarea
          value={intro}
          onChange={(e) => setIntro(e.target.value)}
          rows={6}
          maxLength={3000}
          placeholder={`Introducción en ${locale.toUpperCase()}…`}
          data-testid={`dashboard-epb-intro-${locale}`}
          className="w-full resize-y rounded-xl border border-white/10 bg-black/40 p-3 text-sm leading-relaxed text-white/85 outline-none focus:border-amber-400/50"
        />
        <div className="mt-1 font-mono text-[10px] text-white/35">{intro.length} / 3000</div>
      </Card>

      <Card
        title={`Servicios · ${locale.toUpperCase()}`}
        subtitle="Lista vacía en un idioma = versión canónica de i18n para ese idioma. Título + descripción breve + detalle extendido (acordeón)."
      >
        <ol className="space-y-2">
          {services.map((s, i) => (
            <li
              key={i}
              data-testid={`dashboard-epb-service-${locale}-${i}`}
              className="rounded-lg border border-white/8 bg-black/30 p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[11px] font-black text-amber-400/80">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                    className="rounded-md border border-white/10 p-1 text-white/50 hover:text-white disabled:opacity-30">↑</button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === services.length - 1}
                    className="rounded-md border border-white/10 p-1 text-white/50 hover:text-white disabled:opacity-30">↓</button>
                  <button type="button" onClick={() => removeService(i)}
                    className="rounded-md border border-white/10 p-1 text-white/50 hover:border-red-400/40 hover:text-red-300">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <input
                type="text" value={s.title || ""}
                onChange={(e) => setService(i, { title: e.target.value })}
                placeholder="Título del servicio" maxLength={200}
                className="mt-2 w-full rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400/50"
              />
              <textarea
                value={s.description || ""}
                onChange={(e) => setService(i, { description: e.target.value })}
                rows={2} maxLength={1000}
                placeholder="Descripción breve (siempre visible en la tarjeta)"
                data-testid={`dashboard-epb-service-${locale}-${i}-description`}
                className="mt-2 w-full resize-y rounded-md border border-white/10 bg-black/40 px-3 py-2 text-xs text-white/85 outline-none focus:border-amber-400/50"
              />
              <label className="mt-2 block font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-white/40">
                Detalle extendido (aparece al expandir la tarjeta · acordeón)
              </label>
              <textarea
                value={s.details || ""}
                onChange={(e) => setService(i, { details: e.target.value })}
                rows={5} maxLength={5000}
                placeholder="Contenido detallado que se mostrará al hacer click en el servicio en la página pública."
                data-testid={`dashboard-epb-service-${locale}-${i}-details`}
                className="mt-1 w-full resize-y rounded-md border border-white/10 bg-black/40 px-3 py-2 text-xs leading-relaxed text-white/85 outline-none focus:border-amber-400/50"
              />
              <span className="mt-1 block text-right font-mono text-[9px] text-white/30">
                {(s.details || "").length} / 5000
              </span>
            </li>
          ))}
        </ol>
        <button
          type="button"
          onClick={addService}
          data-testid={`dashboard-epb-service-add-${locale}`}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.04] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/70 transition hover:border-amber-400/40 hover:text-amber-200"
        >
          <Plus className="h-3 w-3" /> Añadir servicio ({locale.toUpperCase()})
        </button>
      </Card>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {msg && <span className="text-xs text-amber-300" data-testid="dashboard-epb-msg">{msg}</span>}
        <button
          type="button" onClick={reset} disabled={saving}
          data-testid="dashboard-epb-reset"
          className="inline-flex items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.04] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white/60 transition hover:text-white"
        >
          Restablecer defaults
        </button>
        <button
          type="button" onClick={save} disabled={saving}
          data-testid="dashboard-epb-save"
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/50 bg-amber-500/[0.14] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.22em] text-amber-200 transition hover:bg-amber-500/[0.22] disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
          Guardar EPB (ES · EN · SK)
        </button>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// LEADS TAB — inbox for consulting requests
// ══════════════════════════════════════════════════════════════════════
const LeadsTab = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/eddy/consulting/leads?include_archived=${includeArchived}`,
        { withCredentials: true }
      );
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);
  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    await axios.put(`${API}/eddy/consulting/leads/${id}`, patch, { withCredentials: true });
    load();
  };

  if (loading) return <TabLoader />;

  return (
    <div className="space-y-4" data-testid="dashboard-leads-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-white/85">
            {items.length} consulta{items.length === 1 ? "" : "s"}
            {unread > 0 && (
              <span className="ml-2 rounded-full border border-amber-400/40 bg-amber-500/[0.1] px-2 py-0.5 font-mono text-[10px] font-bold text-amber-200">
                {unread} sin leer
              </span>
            )}
          </p>
          <p className="mt-0.5 text-xs text-white/40">Formulario público de EPB Global F&B</p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-white/60">
          <input
            type="checkbox" checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="dashboard-leads-include-archived"
          />
          Mostrar archivadas
        </label>
      </div>

      {items.length === 0 ? (
        <Card>
          <p className="text-center text-sm text-white/50">Sin consultas.</p>
        </Card>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li key={it.id} data-testid={`dashboard-lead-${it.id}`}>
              <Card>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-bold text-white">{it.name}</span>
                      {!it.read && (
                        <span className="rounded-full border border-amber-400/40 bg-amber-500/[0.1] px-2 py-0.5 font-mono text-[9px] font-bold text-amber-200">
                          nueva
                        </span>
                      )}
                      {it.archived && (
                        <span className="rounded-full border border-white/15 px-2 py-0.5 font-mono text-[9px] font-bold text-white/40">
                          archivada
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-white/50">
                      <a href={`mailto:${it.email}`} className="hover:text-amber-200">
                        <Mail className="mr-1 inline h-3 w-3" />{it.email}
                      </a>
                      {it.company && <span>· {it.company}</span>}
                      <span className="text-white/30">· {new Date(it.created_at).toLocaleString()}</span>
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {!it.read && (
                      <button
                        type="button" onClick={() => update(it.id, { read: true })}
                        data-testid={`dashboard-lead-read-${it.id}`}
                        title="Marcar como leída"
                        className="rounded-md border border-white/10 p-1.5 text-white/60 transition hover:border-amber-400/40 hover:text-amber-200"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      type="button" onClick={() => update(it.id, { archived: !it.archived })}
                      data-testid={`dashboard-lead-archive-${it.id}`}
                      title={it.archived ? "Restaurar" : "Archivar"}
                      className="rounded-md border border-white/10 p-1.5 text-white/60 transition hover:text-white"
                    >
                      <Inbox className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <p className="mt-3 whitespace-pre-wrap rounded-md border border-white/5 bg-black/30 p-3 text-sm text-white/75">
                  {it.message}
                </p>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// STATS TAB
// ══════════════════════════════════════════════════════════════════════
const StatsTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/eddy/stats`, { withCredentials: true })
      .then(({ data }) => setStats(data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <TabLoader />;
  if (!stats) return <Card><p className="text-sm text-white/50">Sin datos.</p></Card>;

  const kpis = [
    { key: "posts",    label: "Publicaciones", icon: FileText },
    { key: "likes",    label: "Likes",         icon: Heart },
    { key: "comments", label: "Comentarios",   icon: MessageCircle },
    { key: "shares",   label: "Compartidas",   icon: Share2 },
    { key: "views",    label: "Vistas",        icon: Eye },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-stats-tab">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {kpis.map(({ key, label, icon: Icon }) => (
          <div
            key={key}
            data-testid={`dashboard-stat-${key}`}
            className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
          >
            <Icon className="h-4 w-4 text-amber-400/80" />
            <div className="mt-2 font-mono text-2xl font-black text-white">{stats[key] ?? 0}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">{label}</div>
          </div>
        ))}
      </div>

      <Card title="Top publicaciones (por likes)" subtitle="Ámbito: canal RE₿EL by Eddy únicamente">
        {stats.top?.length === 0 ? (
          <p className="text-sm text-white/50">Aún no hay publicaciones para rankear.</p>
        ) : (
          <ul className="space-y-2" data-testid="dashboard-top-posts">
            {stats.top.map((p, i) => (
              <li
                key={p.id}
                data-testid={`dashboard-top-${p.id}`}
                className="flex items-center justify-between gap-3 rounded-md border border-white/5 bg-white/[0.02] px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="w-6 shrink-0 text-center font-mono text-xs font-black text-amber-400/70">#{i + 1}</span>
                  <span className="truncate text-sm text-white/85">{p.title}</span>
                </div>
                <div className="flex shrink-0 gap-3 font-mono text-[10px] text-white/50">
                  <span><Heart className="mr-1 inline h-3 w-3" />{p.likes}</span>
                  <span><MessageCircle className="mr-1 inline h-3 w-3" />{p.comments}</span>
                  <span><Share2 className="mr-1 inline h-3 w-3" />{p.shares}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <p className="text-center font-mono text-[10px] uppercase tracking-[0.22em] text-white/30">
        Ámbito del reporte: sección RE₿EL by Eddy · No incluye datos de otras áreas
      </p>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Shared UI helpers
// ══════════════════════════════════════════════════════════════════════
const Card = ({ title, subtitle, children }) => (
  <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-5 md:p-6">
    {title && (
      <div className="mb-4">
        <h3 className="font-mono text-[11px] font-black uppercase tracking-[0.22em] text-amber-400">{title}</h3>
        {subtitle && <p className="mt-1 text-xs text-white/45">{subtitle}</p>}
      </div>
    )}
    {children}
  </div>
);

const TabLoader = () => (
  <div className="flex items-center justify-center py-14">
    <Loader2 className="h-5 w-5 animate-spin text-amber-400" />
  </div>
);

export default RebelByEddyDashboardPage;
